/**
 * `/borne`: the tablet's idle screen (spec 2026-09-27 §2.4), phone first.
 * `complet`: the rotating QR as the main way in, and a « Pas de téléphone ? »
 * strip along the bottom that opens the typed form; `qr`: the same screen
 * without the strip. It pings every 60 s and refetches the QR every 25 s; an
 * auth failure (revoked, re-kinded, token gone) clears the token and returns
 * to pairing, both here and on a submit.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import type { BorneFormApi } from "~/components/borne/borne-form";
import { BorneForm } from "~/components/borne/borne-form";
import { KioskStrip, PhoneHero } from "~/components/borne/borne-idle-options";
import {
  StationBrand,
  StationError,
  StationPulse,
  StationScreen,
  StationTitle,
} from "~/components/station/station-screen";
import { clearBorneDeviceToken } from "~/lib/borne/device-token";
import { borneClient } from "~/lib/borne/orpc";
import {
  idleLayout,
  newerDisplayMode,
  QR_REFETCH_MS,
  qrIsFresh,
} from "~/lib/borne/qr-refresh";
import { BORNE_PATHS } from "~/lib/borne/route";
import { frenchErrorMessage, isUnauthorizedError } from "~/lib/orpc-error";

export const Route = createFileRoute("/borne/")({
  component: BornePage,
});

const PING_MS = 60_000;

function BornePage() {
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);

  const unpair = useCallback(() => {
    clearBorneDeviceToken();
    void navigate({ to: BORNE_PATHS.pairing, replace: true });
  }, [navigate]);

  const ping = useQuery({
    queryKey: ["borne", "ping"],
    queryFn: () => borneClient.borne.ping(),
    refetchInterval: PING_MS,
    refetchIntervalInBackground: true,
    retry: (failureCount, error) =>
      !isUnauthorizedError(error) && failureCount < 3,
  });

  useEffect(() => {
    if (isUnauthorizedError(ping.error)) unpair();
  }, [ping.error, unpair]);

  const config = ping.data;

  const qr = useQuery({
    queryKey: ["borne", "currentQr"],
    queryFn: () => borneClient.borne.currentQr(),
    enabled: Boolean(config),
    refetchInterval: QR_REFETCH_MS,
    refetchIntervalInBackground: true,
    retry: (failureCount, error) =>
      !isUnauthorizedError(error) && failureCount < 1,
  });

  useEffect(() => {
    if (isUnauthorizedError(qr.error)) unpair();
  }, [qr.error, unpair]);

  // Re-render every 5 s so a QR whose refetches keep failing disappears once
  // it is older than its guaranteed validity.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);

  // currentQr refreshes every 25 s, ping every 60 s: the newer answer wins.
  const layout = idleLayout(
    newerDisplayMode(
      config ? { mode: config.displayMode, at: ping.dataUpdatedAt } : null,
      qr.data ? { mode: qr.data.displayMode, at: qr.dataUpdatedAt } : null,
    ),
  );
  const qrUrl =
    qr.data && qrIsFresh(qr.dataUpdatedAt, now) ? qr.data.url : null;
  const api = useMemo<BorneFormApi | null>(
    () =>
      config
        ? {
            orgName: config.orgName,
            idPolicy: config.walkInIdPolicy,
            privacyLink: "inline",
            companies: () => borneClient.borne.companies(),
            hosts: (unitId, q) => borneClient.borne.hosts({ unitId, q }),
            submit: async (input) => {
              try {
                await borneClient.borne.submit(input);
              } catch (e) {
                // Revoked mid-form: back to pairing (spec §4.1 table).
                if (isUnauthorizedError(e)) unpair();
                throw e;
              }
            },
          }
        : null,
    [config, unpair],
  );

  const closeForm = useCallback(() => setFormOpen(false), []);

  // A tablet switched to `qr` while its form is open drops back to idle, and
  // stays there if the mode later flips back (no half-filled form reappearing).
  // (Adjusting state during render, not in an effect: react.dev "You Might
  // Not Need an Effect".)
  if (formOpen && !layout.showButton) setFormOpen(false);

  if (formOpen && api && layout.showButton) {
    return <BorneForm api={api} onDone={closeForm} />;
  }

  const title = <>Bienvenue{config ? <> chez {config.orgName}.</> : null}</>;

  return (
    // Wider than the pairing screens' column: the steps and the QR sit side
    // by side on a landscape iPad and need the room.
    <StationScreen className="max-w-5xl">
      <StationBrand surface="Borne d'accueil" />

      {ping.isError && !isUnauthorizedError(ping.error) ? (
        <StationError>
          {frenchErrorMessage(
            ping.error,
            "Borne hors ligne. Présentez-vous à l'accueil.",
          )}
        </StationError>
      ) : null}

      {!layout.showButton && !layout.showQr ? (
        <>
          <StationTitle className="mt-7 text-4xl lg:text-5xl">
            {title}
          </StationTitle>
          <p className="text-muted-foreground mt-auto flex items-center justify-center gap-2.5 pt-8 text-base">
            <StationPulse />
            Connexion…
          </p>
        </>
      ) : (
        <>
          <PhoneHero
            title={title}
            url={qrUrl}
            fallback={
              layout.showButton
                ? "QR code indisponible. Remplissez votre fiche sur la borne."
                : "QR code indisponible. Présentez-vous à l'accueil."
            }
          />
          {layout.showButton ? (
            <KioskStrip onStart={() => setFormOpen(true)} />
          ) : null}
        </>
      )}
    </StationScreen>
  );
}
